import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ShieldOff } from "lucide-react";
import { toast } from "sonner";

import { membershipOutcome } from "@/lib/groupMembership";
import { joinPolicy, findGroup, groupName, groupHref } from "@/lib/v3Groups";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { runAction, loadGroup } from "../actions";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "./v3";
import { AreaCard, ResponsibleCard } from "./v3app";
import { SegmentTabs } from "./v3people";

// V3 building blocks for circle views (M14–M16, M69–M76, M120).
export const useGroups = () => useCore("/member/groups", (d) => requiredCollection(d, "groups"));

export function useGroup(id) {
  const res = useGroups();
  return { res, group: res.data ? findGroup(res.data, id) : undefined };
}

const FAIL = { join: "Die Beitrittsanfrage wurde nicht übermittelt", withdraw: "Der Rückzug wurde nicht übermittelt", leave: "Der Austritt wurde nicht übermittelt" };

// POST join/leave, then only a fresh GET /member/groups decides what is shown; the re-check is GET-only.
export function useMembership(listed) {
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const alive = useRef(true);
  const flight = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const group = listed && fresh && String(fresh.id) === String(listed.id) ? fresh : listed;
  const settle = (o, read) => {
    setOutcome(o);
    if (read?.ok && read.group) setFresh(read.group);
    if (o.result === "success") toast.success(o.message);
  };
  const act = async (kind, body) => {
    if (flight.current || !group) return null;
    flight.current = true; setBusy(true);
    const postError = await runAction("post", `/member/groups/${encodeURIComponent(group.id)}/${kind === "withdraw" ? "leave" : kind}`, FAIL[kind], body);
    const read = postError ? null : await loadGroup(group.id);
    flight.current = false;
    if (!alive.current) return null;
    setBusy(false);
    const o = membershipOutcome(kind, postError ? { postError } : read);
    if (o.result !== "failed") settle(o, read);
    return o;
  };
  const recheck = async () => {
    if (flight.current || !group) return;
    flight.current = true; setBusy(true);
    const read = await loadGroup(group.id);
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    settle(membershipOutcome(outcome?.kind || "join", read), read);
  };
  return { group, busy, outcome, act, recheck };
}

// M15/M69/M70 frame: closed/open banner and the three areas as links (Überblick, Regeln, Treffen).
export function GroupFrame({ g, tab, children }) {
  const p = joinPolicy(g);
  return (<>
    <StatusBanner icon={Lock} title={p === "approval" ? "Geschlossener Kreis" : p === "open" ? "Offener Kreis" : "Kreis"} testid="group-banner"
      text="Interne Beiträge und Gespräche werden nur für aufgenommene Mitglieder angezeigt." />
    <SegmentTabs label="Bereiche des Kreises" value={tab} testid="group-tabs"
      items={[["uebersicht", "Überblick", groupHref(g)], ["regeln", "Regeln", groupHref(g, "/regeln")], ["treffen", "Treffen", groupHref(g, "/treffen")]]} />
    <div className="mt-[11px]">{children}</div>
  </>);
}

export const GroupAside = ({ text: body, title }) => <><AreaCard title={title} text={body} testid="group-area" /><ResponsibleCard /></>;

// M120: circle not visible, or members-only area without a confirmed membership; nothing internal is shown.
export function GroupNoAccess({ g }) {
  const navigate = useNavigate();
  const name = g ? groupName(g) : "";
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/kreise"));
  return (
    <div data-m="M120" data-testid="group-no-access">
      <V3Title title="Keine Berechtigung für diesen Kreis" intro="Dieser Kreis ist für deinen aktuellen Zugang nicht freigegeben." testid="group-no-access-title" />
      <V3Columns layout="app" aside={<GroupAside text={name || "Kreis nicht sichtbar"} />} main={<>
        <StatusBanner tone="sand" icon={ShieldOff} title="Kein Kreiszugriff" text={name || "Kreis nicht sichtbar oder nicht vorhanden"} testid="group-no-access-banner" />
        <DetailList>
          <DetailRow label="Bereich" value={name || "Kreis nicht sichtbar oder nicht vorhanden"} testid="group-no-access-area" />
          <DetailRow label="Zugriff" value={g ? "Nur für aufgenommene Mitglieder" : "Nicht erlaubt"} testid="group-no-access-access" />
          <DetailRow label="Interne Inhalte" value="Werden nicht angezeigt" testid="group-no-access-internal" />
          <DetailRow label="Nächster Schritt" value={g ? "Zur erlaubten Kreisvorschau zurückkehren" : "Zur Übersicht der Kreise zurückkehren"} testid="group-no-access-next" />
        </DetailList>
        <V3Actions>
          <Link to={g ? groupHref(g) : "/kreise"} data-testid="group-no-access-preview" className={V3_PRIMARY}>{g ? "Zur Kreisvorschau" : "Zu den Kreisen"}</Link>
          <button type="button" onClick={back} data-testid="group-no-access-back" className={V3_SECONDARY}>Zurück</button>
        </V3Actions>
      </>} />
    </div>
  );
}
